import { Component, type ErrorInfo, type ReactNode } from 'react';
import { erModullastfeil, meldEnGang } from '../api/klientfeil.js';
import { Knapp } from './Knapp.js';

/**
 * Fanger en feil under rendring, så én ødelagt skjerm ikke tar ned resten.
 *
 * Uten denne river React ned hele treet når en komponent kaster, og brukeren
 * sitter igjen med en helt hvit side. Det er den samme innvendingen som gjaldt
 * innloggingen: ett uhell skal ikke se ut som at hele tjenesten er nede.
 *
 * Den må være en klasse. `componentDidCatch` og `getDerivedStateFromError`
 * finnes ikke som kroker — det er den ene tingen React fortsatt bare tilbyr
 * her, og enhver innpakning rundt det ville vært støy.
 *
 * To plasseringer, med hver sin jobb:
 *
 *   `omfang="siden"` ligger INNENFOR skallet, rundt rutene. Menyen,
 *   virksomhetsvelgeren og navigasjonen står igjen, så brukeren kan gå et annet
 *   sted uten å laste noe på nytt. Det er forskjellen på «denne skjermen er
 *   ødelagt» og «programmet er borte».
 *
 *   `omfang="appen"` ligger ytterst. Den fanger det som skjer før skallet
 *   finnes — et feilet oppsett, en feil i selve rammen — og der er full
 *   innlasting det eneste ærlige tilbudet.
 */

interface Props {
  children: ReactNode;
  omfang: 'siden' | 'appen';
  /**
   * Endres denne mens en feil står, nullstilles grensen.
   *
   * Brukes til å sende stien inn: uten det blir feilskjermen stående etter at
   * brukeren har klikket seg videre, fordi grensen ikke vet at noe skjedde.
   */
  nullstillNokkel?: string;
}

interface State {
  feil: unknown;
  /** Kommer fra serveren, og bare hvis meldingen nådde fram. */
  sporingsId: string | null;
}

const TOM: State = { feil: null, sporingsId: null };

export class Feilgrense extends Component<Props, State> {
  override state: State = TOM;

  static getDerivedStateFromError(feil: unknown): Partial<State> {
    return { feil };
  }

  override componentDidCatch(feil: unknown, info: ErrorInfo): void {
    // Konsollen først, og uansett hva serveren svarer. Den er det eneste
    // sporet som finnes når API-et er nede — som er nettopp da en feil i
    // klienten er mest sannsynlig.
    console.error('Feilgrense fanget en renderfeil', feil, info.componentStack);

    void meldEnGang(feil, info.componentStack ?? undefined, window.location.pathname).then(
      (sporingsId) => {
        // Ingen «er jeg fortsatt montert»-vakt. React 18 og nyere advarer ikke
        // om setState på en avmontert komponent, så vakten ville ikke løst noe
        // — den ville bare kastet bort id-en i de tilfellene der React har
        // bygget grensen opp igjen, uten å si fra om det.
        if (sporingsId !== null) this.setState({ sporingsId });
      },
    );
  }

  override componentDidUpdate(forrige: Props): void {
    if (this.state.feil !== null && forrige.nullstillNokkel !== this.props.nullstillNokkel) {
      this.nullstill();
    }
  }

  /*
   * En detalj verdt å vite om nullstillingen over: et klikk i menyen bytter sti
   * OG rendrer den nye skjermen i samme omgang. Er det DEN skjermen som feiler,
   * ser sammenligningen en endret nøkkel, nullstiller, og barna kaster igjen.
   * Feilen fanges altså to ganger for ett klikk.
   *
   * Det er målt og bevisst tålt: `meldEnGang` gjør de to til én innmelding, og
   * brukeren ser det samme. Et forsøk på å unngå den andre runden — ved å
   * sammenligne mot nøkkelen slik den var da feilen oppsto — gjorde koden
   * større uten at en eneste test kunne se forskjell, så den ble tatt ut igjen.
   */

  private nullstill = (): void => {
    this.setState(TOM);
  };

  override render(): ReactNode {
    if (this.state.feil === null) return this.props.children;

    const heleAppen = this.props.omfang === 'appen';
    // En modul som ikke lot seg hente kan ikke repareres ved å tegne skjermen
    // på nytt: `React.lazy` husker det avviste løftet, så en nullstilling
    // kaster umiddelbart igjen uten å spørre nettverket. Knappen må derfor
    // gjøre det ene som virker, ellers er den et tilbud som ikke finnes.
    const nyVersjon = !heleAppen && erModullastfeil(this.state.feil);
    const maaLastes = heleAppen || nyVersjon;
    const { sporingsId } = this.state;

    return (
      <div
        role="alert"
        className={
          heleAppen
            ? 'mx-auto mt-16 max-w-lg rounded-md border border-negativ/40 bg-negativ-svak p-6'
            : 'rounded-md border border-negativ/40 bg-negativ-svak p-6'
        }
      >
        <h1 className="text-lg font-semibold text-negativ">
          {heleAppen
            ? 'Enklere styring klarte ikke å starte'
            : 'Denne skjermen klarte ikke å vises'}
        </h1>
        <p className="mt-2 text-sm text-tekst">
          {heleAppen
            ? 'Det er en feil i programmet, ikke i tallene dine. Ingenting er lagret eller endret.'
            : nyVersjon
              ? 'Det er kommet en ny versjon av Enklere styring mens du hadde siden åpen. Last den inn på nytt, så er du i gang igjen. Ingenting er lagret eller endret.'
              : 'Det er en feil i programmet, ikke i tallene dine. Ingenting er lagret eller endret — de andre skjermene virker fortsatt.'}
        </p>

        {sporingsId === null ? (
          // Ingen id vises når serveren ikke fikk meldingen. En id brukeren kan
          // lese opp, men som ikke står i noen logg, sender feilsøkingen til et
          // blindspor — og får henne til å tro at hun er hjulpet.
          <p className="mt-3 text-xs text-tekst-dempet">
            Skjer det igjen, noter hva du gjorde rett før og gi beskjed.
          </p>
        ) : (
          <p className="mt-3 text-xs text-tekst-dempet">
            Oppgi denne ved henvendelse — Sporings-ID: <code>{sporingsId}</code>
          </p>
        )}

        <div className="mt-4 flex gap-2">
          {maaLastes ? (
            <Knapp
              onClick={() => {
                window.location.reload();
              }}
            >
              Last inn på nytt
            </Knapp>
          ) : (
            <Knapp onClick={this.nullstill}>Prøv igjen</Knapp>
          )}
        </div>
      </div>
    );
  }
}
